import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { ConfigContext, TranslateContext } from "../App";
import { useCategoryContent } from "../hooks/useQueries";
import { useLocation } from "react-router-dom";
import Loading from "../components/Loading/Loading.js";
import { renderCardsSection } from "../functions/renderComponent.js";
import Banner from "../components/Banner/Banner.js";
import { optimizeImageUrl, IMAGE_SIZES } from "../functions/utils.js";

/**
 * Category page component.
 * Displays products or blog posts belonging to a specific category.
 * Uses React Query for cached data fetching.
 */
function Category() {
  const configuration = useContext(ConfigContext);
  const location = useLocation();
  const slug = location.pathname.substring(1);
  const [cards, setCards] = useState([]);
  const [isRendering, setIsRendering] = useState(true);
  const language = useContext(TranslateContext).language;

  // Use React Query hook for cached category data
  const { data: pageContent, isLoading: dataLoading } = useCategoryContent(slug);

  useEffect(() => {
    const processCategoryData = async () => {
      if (!pageContent) return;
      
      setIsRendering(true);
      
      if (pageContent._type === "productCategory") {
        const products = {
          cardProductSection: pageContent.contents,
          cardType: "product-sm",
          hasFilter: true,
        };
        setCards(await renderCardsSection(products, false, false, language, false));
      } else if (pageContent._type === "blogCategory") {
        const blogs = {
          // `renderCardsSection` expects `cardNewsSection` for blog cards.
          cardNewsSection: pageContent.contents,
          cardType: "blog",
          hasFilter: true,
        };
        setCards(await renderCardsSection(blogs, false, false, language, false));
      }
      
      setIsRendering(false);
    };

    processCategoryData();
  }, [pageContent, language]);

  const showImage = pageContent?.showImageOnCategoryPage;
  const isLoading = dataLoading || isRendering;

  if (isLoading || !pageContent) {
    return <Loading />;
  }

  return (
    pageContent && (
      <>
        <Helmet>
          <title>{pageContent.title[language]}</title>
          <meta name="description" content={pageContent.description?.[language]} />
        </Helmet>
        {configuration?.checkoutBanner?.show && (
          <div className="row bg-[#fcf6ef]">
            <div className="row-container">
              <Banner
                type={configuration?.checkoutBanner?.type || "warning"}
                message={configuration?.checkoutBanner?.message}
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="row-container">
            <div className="column">
              <h1 className="h1-title" style={{ color: configuration.colors.secondary.hex }}>
                {pageContent.title[language]}
              </h1>
              {pageContent.image && showImage && (
                <img
                  className="h-[180px] lg:h-[440px] w-full object-cover object-center"
                  src={optimizeImageUrl(pageContent.image?.url, IMAGE_SIZES.full)}
                  alt={pageContent.title[language]}
                />
              )}
              {pageContent.description && <p>{pageContent.description[language]}</p>}
            </div>
            {cards}
          </div>
        </div>
      </>
    )
  );
}

export default Category;
